import { useCallback } from 'react';
import { spaceApi } from '../services/api';
import { useToast } from '../components/ui/Toast';
import type { AuiAction } from '../types/canvases';

/**
 * Returns a stable callback that dispatches an AUI surface action to the
 * given agent via `spaceApi(spaceId).agents.dispatchAction`. Used by every
 * conversation surface that hosts an AUI canvas (Builder, Sandbox, …)
 * so the action routing is identical everywhere.
 *
 * The callback signature matches `CanvasPanel`'s `onAction` prop:
 * `(action, surfaceName, canvasId) => Promise<void>`.
 *
 * NOTE: the backend `/dispatch-action` endpoint keys off the agent-provided
 * `surfaceName` (not the platform `srf-…` surfaceId) and rejects the request
 * with "surfaceName is required" when it's absent — so the canvas chain threads
 * `surface.surfaceName` through, not `surfaceId`.
 */
export function useDispatchAction(
  agentId: string | undefined,
  spaceId: string | undefined,
  conversationIdRef:
    | React.RefObject<string | undefined>
    | React.RefObject<string | null>
    | React.MutableRefObject<string | null>
    | React.MutableRefObject<string | undefined>
    | undefined,
) {
  const toast = useToast();
  return useCallback(
    async (action: AuiAction, surfaceName: string, canvasId: string) => {
      if (!agentId) return;
      if (!spaceId) {
        toast.error('Cannot dispatch action: space not yet resolved');
        return;
      }
      if (!surfaceName) {
        toast.error('Cannot save: this surface has no name yet');
        return;
      }
      try {
        await spaceApi(spaceId).agents.dispatchAction(agentId, {
          surfaceName,
          canvasId,
          action,
          conversationId: conversationIdRef?.current ?? undefined,
        });
        toast.success(`Action '${action.label}' sent`);
      } catch (err) {
        toast.error(err instanceof Error ? err.message : 'Failed to send action');
        throw err;
      }
    },
    [agentId, spaceId, conversationIdRef, toast],
  );
}
